import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Download, Eye, Receipt, X } from 'lucide-react';
import { Button } from '../ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const BACKEND = process.env.REACT_APP_BACKEND_URL;
const money = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);
const when = (d) => (d ? new Date(d).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }) : '—');

// Customer-facing list of payment receipts with inline PDF preview + download.
export const ReceiptsList = ({ token }) => {
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [preview, setPreview] = useState(null); // {payment_id, invoice_number, pdf_url}

  useEffect(() => {
    if (!token) return;
    axios.get(`${API_URL}/user/receipts`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setReceipts(Array.isArray(data) ? data : []))
      .catch(() => setReceipts([]))
      .finally(() => setLoading(false));
  }, [token]);

  return (
    <>
      <div className="mt-6 bg-white rounded-lg border border-neutral-200" data-testid="receipts-section">
        <div className="px-5 py-4 border-b border-neutral-200 flex items-center gap-2">
          <Receipt className="w-4 h-4 text-emerald-600" />
          <h3 className="font-medium">Payment Receipts</h3>
          <span className="text-neutral-400 text-sm">({receipts.length})</span>
        </div>
        {loading ? (
          <p className="text-neutral-500 text-sm text-center py-6">Loading receipts…</p>
        ) : receipts.length === 0 ? (
          <p className="text-neutral-500 text-sm text-center py-6" data-testid="receipts-empty">No payments recorded yet</p>
        ) : (
          <div className="divide-y divide-neutral-100">
            {receipts.map((r) => (
              <div key={r.payment_id} className="px-5 py-3 flex items-center justify-between gap-3" data-testid={`receipt-${r.payment_id}`}>
                <div className="min-w-0">
                  <p className="font-medium text-sm">{money(r.amount)} · {r.invoice_number}</p>
                  <p className="text-xs text-neutral-500 truncate">{when(r.recorded_at)} · {(r.method || 'other').replace(/_/g, ' ')}{r.reference ? ` · ${r.reference}` : ''}</p>
                </div>
                <div className="flex gap-1.5 flex-shrink-0">
                  <Button variant="outline" size="sm" className="gap-1.5" onClick={() => setPreview(r)} data-testid={`receipt-preview-${r.payment_id}`}>
                    <Eye className="w-3.5 h-3.5" /> View
                  </Button>
                  <Button asChild variant="outline" size="sm" className="gap-1.5">
                    <a href={`${BACKEND}${r.pdf_url}`} target="_blank" rel="noopener noreferrer" data-testid={`receipt-pdf-${r.payment_id}`}>
                      <Download className="w-3.5 h-3.5" />
                    </a>
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Inline PDF Preview Dialog */}
      <Dialog open={!!preview} onOpenChange={(v) => { if (!v) setPreview(null); }}>
        <DialogContent className="max-w-3xl h-[80vh] flex flex-col p-0 gap-0" data-testid="receipt-preview-dialog">
          <DialogHeader className="px-5 py-3 border-b border-neutral-200 flex-shrink-0">
            <div className="flex items-center justify-between">
              <DialogTitle className="text-base font-medium flex items-center gap-2">
                <Receipt className="w-4 h-4 text-emerald-600" />
                Receipt — {preview?.invoice_number}
              </DialogTitle>
              <div className="flex items-center gap-2">
                <Button asChild variant="outline" size="sm" className="gap-1.5">
                  <a href={`${BACKEND}${preview?.pdf_url}`} target="_blank" rel="noopener noreferrer" data-testid="receipt-preview-download">
                    <Download className="w-3.5 h-3.5" /> Download
                  </a>
                </Button>
              </div>
            </div>
          </DialogHeader>
          <div className="flex-1 min-h-0 bg-neutral-100">
            {preview && (
              <iframe
                src={`${BACKEND}${preview.pdf_url}`}
                className="w-full h-full border-0"
                title={`Receipt ${preview.invoice_number}`}
                data-testid="receipt-preview-iframe"
              />
            )}
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default ReceiptsList;
